import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { InputFieldComponent } from '../../../shared/components/form/input/input-field.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { AuthService, TwoFactorSetupResponse, TwoFactorStatusResponse } from '../../../shared/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-two-factor-setup',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, InputFieldComponent, ModalComponent],
  templateUrl: './two-factor-setup.component.html'
})
export class TwoFactorSetupComponent implements OnInit {
  @Input() forceSetupRequired = false;
  @Input() identifierInput = '';
  @Input() setupTokenInput = '';
  @Input() redirectToLoginAfterSuccess = false;

  otpauthUrl = '';
  secret = '';
  qrImageUrl = '';
  verificationCode = '';

  // Modal and step navigation
  // Modal and step navigation
  showSetupModal = false;
  currentStep: 'scan' | 'verify' | 'confirm-exit' = 'scan';
  previousStep: 'scan' | 'verify' = 'scan'; // Track previous step to restore if cancelled

  loading = false;
  verifying = false;
  disabling = false;
  statusLoading = false;
  setupComplete = false;
  errorMessage = '';
  twoFactorError = '';
  identifier = '';
  username = '';
  password = '';
  setupToken = '';
  showPassword = false;
  showDisablePassword = false;
  twoFactorEnabled = false;
  emailOtpEnabled = false;
  disablePassword = '';
  showSetupPrompt = false;
  emailOtpDisablePassword = '';
  emailOtpPassword = '';
  isEmailOtpPromptOpen = false;
  emailOtpEnabling = false;
  emailOtpDisabling = false;

  constructor(
    private authService: AuthService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    const currentUser = this.authService.getCurrentUser();
    const inputIdentifier = this.identifierInput.trim();
    const inputSetupToken = this.setupTokenInput.trim();

    this.identifier = currentUser?.email || inputIdentifier || '';
    this.username = currentUser?.name || this.identifier;
    this.setupToken = inputSetupToken || '';

    if (this.setupToken.trim()) {
      this.forceSetupRequired = true;
    }

    if (this.identifier) {
      this.loadStatus();
    }
  }

  loadStatus(): void {
    this.statusLoading = true;
    this.authService.getTwoFactorStatus(this.setupToken).subscribe({
      next: (status: TwoFactorStatusResponse) => {
        this.twoFactorEnabled = !!status?.twoFactorEnabled;
        this.emailOtpEnabled = !!status?.emailOtpEnabled;
        this.forceSetupRequired = status?.forceTwoFactorSetup === true;

        if (this.forceSetupRequired && !this.setupToken.trim()) {
          this.setupToken = this.authService.getPendingForceTwoFactorSetupToken() || '';
        }

        this.statusLoading = false;
      },
      error: () => {
        this.twoFactorEnabled = false;
        this.emailOtpEnabled = false;
        this.statusLoading = false;
      }
    });
  }

  loadSetup(skipPasswordCheck = false): void {
    this.errorMessage = '';

    if (!skipPasswordCheck && !this.password.trim()) {
      this.errorMessage = 'Password is required to start 2FA setup.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.setupComplete = false;

    this.authService.startTwoFactorSetup(
      this.identifier.trim(),
      this.username.trim(),
      skipPasswordCheck ? undefined : this.password,
      skipPasswordCheck ? this.setupToken : undefined
    ).subscribe({
      next: (response: TwoFactorSetupResponse) => {
        this.otpauthUrl = response?.otpauthUrl || '';
        this.secret = this.extractSecret(this.otpauthUrl, response?.secret);
        this.qrImageUrl = this.otpauthUrl ? this.buildQrCodeUrl(this.otpauthUrl) : '';
        this.loading = false;
        this.showSetupPrompt = false;
        this.showSetupModal = true;
        this.currentStep = 'scan';
      },
      error: (error: Error) => {
        this.loading = false;
        this.errorMessage = error?.message || 'Unable to start two-factor setup.';
      }
    });
  }

  openSetupPrompt(): void {
    if (this.setupToken.trim()) {
      this.loadSetup(true);
      return;
    }

    this.showSetupPrompt = true;
    this.errorMessage = '';
  }

  cancelSetupPrompt(): void {
    this.showSetupPrompt = false;
    this.password = '';
    this.errorMessage = '';
  }

  verifyCode(code?: string): void {
    const token = (code ?? this.verificationCode).trim();
    if (!token) {
      this.toastService.error('Enter the 6-digit code from your authenticator app.', undefined, 'Verification required');
      return;
    }

    this.verifying = true;
    this.errorMessage = '';
    this.twoFactorError = '';

    this.authService.verifyTwoFactorCode(
      this.identifier.trim(),
      this.username.trim(),
      token,
      this.forceSetupRequired ? undefined : this.password,
      this.forceSetupRequired ? this.setupToken : undefined
    ).subscribe({
      next: () => {
        this.verifying = false;
        this.setupComplete = true;
        this.twoFactorEnabled = true;
        this.authService.clearPendingForceTwoFactorSetupToken();
        this.handlePostSetupSuccess(() => {
          this.forceCloseModal();
          this.loadStatus();
        });
      },
      error: (error: Error) => {
        this.verifying = false;
        this.twoFactorError = error?.message || 'Unable to verify the provided code.';
      }
    });
  }

  copyToClipboard(value: string, label: string): void {
    if (!value) {
      return;
    }

    navigator.clipboard?.writeText(value)
      .then(() => this.toastService.success(`${label} copied to clipboard.`))
      .catch(() => this.toastService.error(`Unable to copy ${label.toLowerCase()}.`));
  }

  onPromptBack(): void {
    this.twoFactorError = '';
    this.currentStep = 'scan';
  }

  goToVerifyStep(): void {
    this.currentStep = 'verify';
  }

  closeSetupModal(): void {

    const hasScannedLikely = this.currentStep === 'verify' || this.otpauthUrl;

    if (hasScannedLikely && !this.setupComplete) {
      // Switch view to confirm exit instead of opening new modal
      if (this.currentStep !== 'confirm-exit') {
        this.previousStep = this.currentStep as 'scan' | 'verify';
        this.currentStep = 'confirm-exit';
      }
      return;
    }

    this.forceCloseModal();
  }

  confirmClose(): void {
    this.forceCloseModal();
  }

  cancelClose(): void {
    // Return to previous step
    this.currentStep = this.previousStep;
  }

  private forceCloseModal(): void {
    this.showSetupModal = false;
    this.currentStep = 'scan';
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleDisablePasswordVisibility(): void {
    this.showDisablePassword = !this.showDisablePassword;
  }

  disableTwoFactor(): void {
    if (this.forceSetupRequired) {
      this.toastService.error('Two-factor authentication is required for your account.', undefined, '2FA required');
      return;
    }

    if (!this.disablePassword.trim()) {
      this.toastService.error('Enter your current password to disable 2FA.');
      return;
    }

    this.disabling = true;
    this.errorMessage = '';

    this.authService.disableTwoFactor(
      this.identifier.trim(),
      this.username.trim(),
      this.disablePassword.trim()
    ).subscribe({
      next: () => {
        this.disabling = false;
        this.twoFactorEnabled = false;
        this.setupComplete = false;
        this.otpauthUrl = '';
        this.secret = '';
        this.qrImageUrl = '';
        this.verificationCode = '';
        this.password = '';
        this.disablePassword = '';
        this.loadStatus();
      },
      error: (error: Error) => {
        this.disabling = false;
        this.errorMessage = error?.message || 'Unable to disable two-factor authentication.';
      }
    });
  }

  openEmailOtpPrompt(): void {
    this.isEmailOtpPromptOpen = true;
    this.emailOtpPassword = '';
  }

  cancelEmailOtpPrompt(): void {
    this.isEmailOtpPromptOpen = false;
    this.emailOtpPassword = '';
  }

  startEmailOtpSetup(): void {
    if (!this.identifier.trim()) {
      this.toastService.error('Unable to identify current user.');
      return;
    }

    const setupToken = this.setupToken.trim();
    const password = this.emailOtpPassword.trim();
    if (!this.forceSetupRequired && !password) {
      this.toastService.error('Current password is required.');
      return;
    }

    this.emailOtpEnabling = true;
    this.authService.enableEmailOtp(this.identifier.trim(), this.forceSetupRequired ? undefined : password, this.forceSetupRequired ? setupToken : undefined).subscribe({
      next: () => {
        this.emailOtpEnabling = false;
        this.isEmailOtpPromptOpen = false;
        this.emailOtpPassword = '';
        this.handlePostSetupSuccess(() => this.loadStatus());
      },
      error: () => {
        this.emailOtpEnabling = false;
      }
    });
  }

  disableEmailOtp(): void {
    if (!this.identifier.trim()) {
      this.toastService.error('Unable to identify current user.');
      return;
    }

    if (!this.emailOtpDisablePassword.trim()) {
      this.toastService.error('Enter your current password to disable Email OTP.');
      return;
    }

    this.emailOtpDisabling = true;
    this.authService.disableEmailOtp(this.identifier.trim(), this.emailOtpDisablePassword.trim()).subscribe({
      next: () => {
        this.emailOtpDisabling = false;
        this.emailOtpDisablePassword = '';
        this.loadStatus();
      },
      error: () => {
        this.emailOtpDisabling = false;
      }
    });
  }

  private buildQrCodeUrl(otpauthUrl: string): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(otpauthUrl)}`;
  }

  private extractSecret(otpauthUrl: string, fallback?: string): string {
    try {
      const url = new URL(otpauthUrl);
      return url.searchParams.get('secret') || fallback || '';
    } catch {
      return fallback || '';
    }
  }

  private handlePostSetupSuccess(onLocalSuccess: () => void): void {
    if (this.redirectToLoginAfterSuccess) {
      this.authService.logout();
      return;
    }

    onLocalSuccess();
  }
}
